import { Box, Grid, Link, Stack, Typography, useTheme } from "@mui/material";
import Button from "components/Button";
import AuthLeftPanel from "./AuthLeftSide";
import i18n from "localization/i18n";
import { useThemeColors } from "theme";
import useIsSmUp from "hooks/useIsSmUp";
import ForgetPasswordIcon from "components/icons/ForgetPasswordIcon";
import StandardTextField from "components/general/StandardTextField";
import { useForm } from "react-hook-form";
import useTranslate from "hooks/useTranslate";
import { Link as RouterLink } from "react-router";
import { useEffect, useState } from "react";
import CheckEmailIcon from "components/icons/ResetPasswordCheckEmailIcon";
import useAmnNavigate from "hooks/useAmnNavigate";
import { Snackbar } from "components/general/Snackbar";
import userApi from "adminPanel/user/userApi";
import reportHttpErrors from "helpers/reportHttpErrors";
import { setOrganizationId } from "adminPanel/user/userSlice";
import { useAppDispatch } from "app/hooks";
import patterns from "constants/patterns";
import getOrganization from "helpers/getOrganization";

export interface IForgetPasswordFormData {
  email: string;
  organizationID: string;
}

export default function ForgetPasswordPage() {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<IForgetPasswordFormData>({
    defaultValues: { organizationID: getOrganization() },
  });

  const { translate, messageTrans } = useTranslate("shared.forgetPassword");
  const { amnNavigate } = useAmnNavigate();
  const dispatch = useAppDispatch();
  const colors = useThemeColors();
  const theme = useTheme();
  const isDarkTheme = theme.palette.mode === "dark";
  const isSmUp = useIsSmUp();
  const [emailSent, setEmailSent] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const style = {
    container: {
      height: "100vh",
    },
    cardHeader: {
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1,
    },
    containerLogin: {
      display: "flex",
      justifyContent: "center",
      right: 0,
      alignItems: "center",
    },
    containerMobileLog: {
      backgroundImage: "url('/images/BGMobile.svg')",
      backgroundRepeat: "no-repeat",
      backgroundSize: "contain",
      justifyContent: "center",
      alignItems: "center",
      paddingX: 2,
      height: "85dvh",
    },
    cardContent: {
      width: isSmUp ? "35vw" : "100dvw",
    },

    title: {
      fontSize: 28,
      fontWeight: 600,
      mb: 1,
    },
    Link: {
      textDecoration: "none",
      color: colors.brand.primary,
      fontWeight: 500,
      fontSize: 16,
      display: "inline-block",
      alignSelf: "center",
      cursor: "pointer",
    },
    langSelect: isSmUp
      ? {
          color: colors.brand.primary,
        }
      : {
          position: "absolute",
          top: 22,
          right: i18n.language == "en" ? 5 : "unset",
          left: i18n.language == "ar" ? 5 : "unset",
          color: "white",
        },
    warning: {
      fontSize: 14,
      color: colors.status.error,
    },
  };

  useEffect(() => {
    if (!watch("email")) {
      setEmailSent(false);
    }
  });

  const onSubmit = (data: IForgetPasswordFormData) => {
    setDisabled(true);
    dispatch(setOrganizationId(watch("organizationID")));
    userApi
      .requestForgetPassword(data.email)
      .then(() => {
        setEmailSent(true);
      })
      .catch((e) => {
        reportHttpErrors(e.response, Snackbar);
      })
      .finally(() => {
        setDisabled(false);
      });
  };

  const resendLink = () => {
    setDisabled(true);
    userApi
      .requestForgetPassword(watch("email"))
      .then(() => {
        setEmailSent(true);
        Snackbar(translate("linkResentSuccessfully"), "success");
      })
      .catch((e) => {
        reportHttpErrors(e.response, Snackbar);
      })
      .finally(() => {
        setDisabled(false);
      });
  };

  const cardHeader = emailSent ? (
    <Stack sx={style.cardHeader}>
      <Box
        sx={{
          width: 88,
          height: 88,
          borderRadius: "16px",
          bgcolor: isDarkTheme ? "#283D3C" : "#E4F5F4",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 1,
        }}
      >
        <CheckEmailIcon />
      </Box>
      <Typography sx={style.title}>{translate("checkYourEmail")}</Typography>
      <Stack
        sx={{
          alignItems: "center",
          gap: 3,
        }}
      >
        <Typography
          color="textPrimary"
          sx={{
            paddingLeft: 0.5,
            textAlign: isSmUp ? "center" : "",
          }}
          variant="bodyMediumRegular"
        >
          {translate("checkYourEmailMsg")}
          <br></br>
          <Typography color="textPrimary" variant="bodyMedium">
            {watch("email")}{" "}
          </Typography>
        </Typography>

        <Typography
          color="textSecondary"
          sx={{
            textAlign: isSmUp ? "center" : "",
          }}
          variant="bodyMediumRegular"
        >
          {translate("CheckYourInboxMsg")}
        </Typography>
      </Stack>
    </Stack>
  ) : (
    <Stack sx={style.cardHeader}>
      <Box
        sx={{
          width: 88,
          height: 88,
          borderRadius: "16px",
          bgcolor: isDarkTheme ? "#2F2B52" : "#EFEDFF",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 1,
        }}
      >
        <ForgetPasswordIcon />
      </Box>
      <Typography sx={style.title}>{translate("forgetPassword")}</Typography>
      <Stack
        sx={{
          alignItems: "center",
        }}
      >
        <Typography
          color="textSecondary"
          sx={{
            paddingLeft: 0.5,
            textAlign: isSmUp ? "center" : "",
          }}
          variant="bodyMediumRegular"
        >
          {translate("forgetPasswordMsg")}
        </Typography>
      </Stack>
    </Stack>
  );

  const cardContent = !emailSent ? (
    <form>
      <Stack sx={style.cardContent}>
        {cardHeader}
        <StandardTextField
          margin="normal"
          id="organizationID"
          size="small"
          variant="outlined"
          name="organizationID"
          value={watch("organizationID") ?? ""}
          fullWidth
          autoFocus
          inputProps={register("organizationID", {
            required: {
              value: true,
              message: "requiredError",
            },
            validate: (value) =>
              value !== ":organizationId" || "invalidOrganizationId",
          })}
          data-testid="forget-password-organization-id"
          label={translate("organizationID")}
          error={Boolean(errors.organizationID)}
          helperText={messageTrans(errors.organizationID?.message as string)}
        />
        <StandardTextField
          margin="normal"
          id="email"
          size="small"
          variant="outlined"
          type="text"
          name="email"
          value={watch("email") ?? ""}
          fullWidth
          inputProps={register("email", {
            required: {
              value: true,
              message: "requiredError",
            },
            pattern: {
              value: patterns.email,
              message: "invalidEmail",
            },
          })}
          data-testid="forget-password-email-field"
          label={translate("emailAddress")}
          error={Boolean(errors.email)}
          helperText={messageTrans(errors.email?.message as string)}
        />
        <Stack
          sx={{
            gap: 3,
          }}
        >
          <Button
            fullWidth
            onClick={handleSubmit(onSubmit)}
            type="submit"
            disableElevation
            variant="primary"
            disabled={disabled}
            data-testid="forget-password-submit"
            sx={{
              mt: 2,
            }}
          >
            {translate("continue")}
          </Button>

          <Box
            sx={{
              width: "100%",
              height: "1px",
              background: `repeating-linear-gradient(${
                i18n.language == "ar" ? "to left" : "to right"
              }, ${colors.border.primary} 0 9px, transparent 9px 17px)`,
            }}
          />

          <Link
            component={RouterLink}
            to={`${
              watch("organizationID") ? `/${watch("organizationID")}` : ""
            }/login`}
            underline="none"
            sx={style.Link}
          >
            {translate("backToLogin")}
          </Link>
        </Stack>
      </Stack>
    </form>
  ) : (
    <Stack>
      {cardHeader}
      <Stack
        sx={{
          gap: 2.5,
        }}
      >
        <Button
          onClick={() => amnNavigate("/login")}
          type="submit"
          variant="primary"
          data-testid="forget-password-back-to-login"
          sx={{
            mt: 2,
          }}
        >
          {translate("backToLogin")}
        </Button>
        <Button
          onClick={resendLink}
          type="submit"
          variant="outlined"
          disabled={disabled}
          data-testid="forget-password-resend-link"
        >
          {translate("resendLink")}
        </Button>
      </Stack>
    </Stack>
  );

  return isSmUp ? (
    <Grid container sx={style.container}>
      <Grid size={6}>
        <AuthLeftPanel />
      </Grid>
      <Grid size={6} sx={style.containerLogin}>
        {cardContent}
      </Grid>
    </Grid>
  ) : (
    <Grid container sx={style.containerMobileLog}>
      {cardContent}
    </Grid>
  );
}
